import { useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import {
  Body,
  Button,
  Dialog,
  DialogFooter,
  DialogSection,
  Field,
  I,
  Input,
} from '@leanin/ui';

export interface SchedulePostButtonProps {
  readonly disabled?: boolean;
  readonly pending?: boolean;
  /** Called with the chosen instant as a UTC ISO string. `datetime-local`
   *  resolves in the viewer's local timezone; `.toISOString()` normalizes it,
   *  matching how the server stores publish_at (absolute instant). */
  readonly onSchedule: (publishAtIsoUtc: string) => Promise<void> | void;
}

/** Secondary composer affordance: opens a date/time dialog to schedule the
 *  post for later. Sits beside the primary "Post" button, which still posts
 *  immediately in one click. Mirrors broadcasts' ScheduleSendButton dialog. */
export function SchedulePostButton({
  disabled,
  pending,
  onSchedule,
}: SchedulePostButtonProps) {
  const t = useTranslations('Feed');
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const openDialog = () => {
    setError(null);
    setValue('');
    setOpen(true);
  };

  const confirm = async () => {
    if (value.length === 0) {
      setError(t('composer.schedule.errors.pick'));
      return;
    }
    const target = new Date(value);
    if (Number.isNaN(target.getTime())) {
      setError(t('composer.schedule.errors.invalid'));
      return;
    }
    if (target.getTime() <= Date.now()) {
      setError(t('composer.schedule.errors.future'));
      return;
    }
    await onSchedule(target.toISOString());
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="secondary"
        size="md"
        icon={I.cal}
        onClick={openDialog}
        disabled={disabled || pending}
      >
        {t('composer.schedule.button')}
      </Button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('composer.schedule.dialogTitle')}
        description={t('composer.schedule.dialogDescription')}
        size="sm"
      >
        <DialogSection gap="md">
          <Field label={t('composer.schedule.dateLabel')} required>
            <Input
              type="datetime-local"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              autoFocus
            />
          </Field>
          {error !== null ? (
            <Body size="sm" color="destructive">
              {error}
            </Body>
          ) : null}
        </DialogSection>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={pending}>
            {t('composer.cancel')}
          </Button>
          <Button
            variant="primary"
            iconRight={I.cal}
            onClick={() => void confirm()}
            loading={pending}
            disabled={pending}
          >
            {t('composer.schedule.confirm')}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
}
